import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Bell, Check, UserPlus, UserMinus, AlertTriangle } from 'lucide-react';

const API = '/api';

/**
 * CAMPANA VIOLETA — usuarios ONLINE (Club de pago).
 * Ann 2026-08-11: "que sea la campana violeta, así sabré que se trata de los usuarios online".
 * Violeta = movimiento del Club vía Stripe: ALTA (verde), BAJA o IMPAGO (rojo).
 * Se alimenta de /notificaciones/club-online (categoria='club_online', lo pone el webhook).
 * El email a Dirección lo manda el backend a la vez; esto es la parte visual dentro del CRM.
 */
const VioletNotificationBell = () => {
  const [avisos, setAvisos] = useState([]);
  const [noLeidas, setNoLeidas] = useState(0);
  const [open, setOpen] = useState(false);
  const [shake, setShake] = useState(false);
  const prev = useRef(0);
  const ref = useRef(null);

  const fetchAvisos = async () => {
    try {
      const res = await axios.get(`${API}/notificaciones/club-online`);
      const list = res.data?.avisos || [];
      const nNuevas = res.data?.no_leidas || 0;
      if (nNuevas > prev.current && prev.current !== 0) {
        setShake(true);
        setTimeout(() => setShake(false), 1200);
      }
      prev.current = nNuevas;
      setAvisos(list);
      setNoLeidas(nNuevas);
    } catch (e) {
      /* silencioso: la campana nunca debe romper la barra */
    }
  };

  useEffect(() => {
    fetchAvisos();
    const t = setInterval(fetchAvisos, 60000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const fuera = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', fuera);
    return () => document.removeEventListener('mousedown', fuera);
  }, []);

  const marcarLeida = async (id) => {
    try {
      await axios.post(`${API}/notificaciones/${id}/marcar-leida`);
      setAvisos((prevA) => prevA.map((a) => (a.id === id ? { ...a, leida: true } : a)));
      setNoLeidas((n) => Math.max(0, n - 1));
    } catch (e) { /* silencioso */ }
  };

  const iconoTipo = (tipo) => {
    if (tipo === 'club_alta') return <UserPlus className="w-4 h-4 text-emerald-600" />;
    if (tipo === 'club_impago') return <AlertTriangle className="w-4 h-4 text-red-600" />;
    return <UserMinus className="w-4 h-4 text-red-600" />; // club_baja
  };

  const fecha = (iso) => {
    try { return new Date(iso).toLocaleString('es-ES', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }); }
    catch { return ''; }
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((o) => !o)}
        title="Avisos del Club online (usuarios que pagan por Stripe)"
        className={`relative p-2 rounded-full hover:bg-purple-50 transition-colors ${shake ? 'animate-bounce' : ''}`}
      >
        <Bell className="w-6 h-6 text-purple-700" />
        {noLeidas > 0 && (
          <span className="absolute -top-0.5 -right-0.5 bg-purple-700 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1">
            {noLeidas > 99 ? '99+' : noLeidas}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-xl border border-purple-100 z-50 max-h-[70vh] overflow-hidden flex flex-col">
          <div className="bg-purple-700 text-white px-4 py-3 flex items-center gap-2">
            <Bell className="w-4 h-4" />
            <h3 className="font-bold text-sm">Club online · altas y bajas</h3>
          </div>

          <div className="overflow-y-auto">
            {avisos.length === 0 && (
              <p className="text-sm text-gray-500 text-center py-8 px-4">
                No hay movimientos del Club por ahora.
              </p>
            )}
            {avisos.map((a) => (
              <div
                key={a.id}
                className={`px-4 py-3 border-b border-gray-100 flex gap-3 ${a.leida ? 'opacity-60' : 'bg-purple-50/40'}`}
              >
                <div className="mt-0.5">{iconoTipo(a.tipo)}</div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-gray-900">{a.titulo}</p>
                  <p className="text-xs text-gray-600 break-words">{a.mensaje}</p>
                  <p className="text-[11px] text-gray-400 mt-1">{fecha(a.created_at)}</p>
                </div>
                {!a.leida && (
                  <button
                    onClick={() => marcarLeida(a.id)}
                    title="Marcar como leído"
                    className="self-start text-purple-700 hover:bg-purple-100 rounded p-1"
                  >
                    <Check className="w-4 h-4" />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default VioletNotificationBell;
